import { FC, useState } from 'react';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import * as StepperLayout from 'layouts/StepperLayout';

import commonLocales from '../../common/locales';
import * as CommonStyles from '../../common/styles';
import NextButton from '../../components/NextButton';
import { useMandateCreationFlowContext } from '../../hooks/useMandateCreationFlowContext';
import locales from './locales';

const B2B_MANDATES_FAQ_LINK = 'https://help.shine.fr/fr/articles/2180615';

type Scheme = 'is-b2b' | 'is-not-b2b';

function mapSchemeFromBoolean(value: boolean | undefined): Scheme | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value ? 'is-b2b' : 'is-not-b2b';
}

const StepMandateSchemeChoice: FC<StepProps> = ({ goNext }) => {
  const { setSharedState, sharedState } = useMandateCreationFlowContext();
  const { bankAccounts, error, loading } = useCompanyBankAccounts();
  const { isB2bMandate: initialIsB2bMandate } = sharedState;

  const [scheme, setScheme] = useState(
    mapSchemeFromBoolean(initialIsB2bMandate),
  );

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView />;
  }

  const onSubmit = () => {
    if (scheme === undefined) {
      return;
    }
    const isB2bMandate = scheme === 'is-b2b';
    setSharedState({
      ...sharedState,
      isB2bMandate,
    });
    const nextStep =
      bankAccounts.length > 1 ? 'select-from-bank-account' : 'info-form';
    goNext({ key: isB2bMandate ? nextStep : 'nothing-to-do' });
  };

  return (
    <StepperLayout.Root>
      <StepperLayout.Body>
        <CommonStyles.FormWrapper onValidSubmit={onSubmit}>
          <CommonStyles.HeaderContainer>
            <BackButton />
          </CommonStyles.HeaderContainer>

          <StepperLayout.BodyScrollableSection>
            <Heading marginBottom="space-8" size="lg">
              {locales.title}
            </Heading>
            <Text marginBottom="space-16">{locales.subTitle}</Text>

            <Button
              alignSelf="flex-start"
              as="a"
              href={B2B_MANDATES_FAQ_LINK}
              icon="link"
              marginBottom="space-24"
              rel="noopener noreferrer"
              target="_blank"
              variant="inline-secondary"
            >
              {locales.learnMoreButton}
            </Button>

            <SunshineCard.RadioGroup
              marginBottom="space-16"
              onChange={(value: Scheme) => {
                setScheme(value);
              }}
              value={scheme}
            >
              <SunshineCard.Group>
                <SunshineCard
                  title={locales.radioIsB2b}
                  value="is-b2b"
                  variant="radio"
                />
                <SunshineCard
                  title={locales.radioIsNotB2b}
                  value="is-not-b2b"
                  variant="radio"
                />
              </SunshineCard.Group>
            </SunshineCard.RadioGroup>
          </StepperLayout.BodyScrollableSection>

          <CommonStyles.FooterContainer>
            <NextButton isDisabled={!scheme} type="submit">
              {commonLocales.nextButton}
            </NextButton>
          </CommonStyles.FooterContainer>
        </CommonStyles.FormWrapper>
      </StepperLayout.Body>
    </StepperLayout.Root>
  );
};

export default StepMandateSchemeChoice;
